/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  CompanionUploadRequest, UppyComponentEvent, UppyComponentEventStatus, UppyComponentEventType,
} from '../types';

/**
 * Builds a CompanionUploadRequest from a response from companion upload.
 *
 * @param response the response from the companion upload
 * @param folderId optional id if the asset should be uploaded to a folder.
 */
export function buildCompanionUploadRequest(response: any, folderId?: string): CompanionUploadRequest {
  // The account id from the uploaded asset. Getting it from the response as an extra check.
  const accountId: string = response.meta.key.split('/')[0];

  // The uniqueId created by the companion app that is part of the S3 path.
  const uniqueId: string = response.meta.key.split('/')[1];

  // The height of a video/ image, if it exists.
  const height: number = response.data.custom?.imageHeight || response.data.custom?.videoHeight;

  // The width of a video/ image, if it exists.
  const width: number = response.data.custom?.imageWidth || response.data.custom?.videoWidth;

  // The video length in milliseconds, if it exists.
  const videoLengthInMillis: number = Number(response.data.custom?.videoDurationMillis) || null;

  return {
    fileContentType: response.meta['content-type'],
    fileExtension: response.extension,
    fileName: response.name,
    fileSize: response.size || 0,
    source: response.source,
    folderId,
    accountId,
    uniqueId,
    height,
    width,
    videoLengthInMillis,
  };
}

/**
 * Builds a UppyComponentEvent for the onComplete event from a response from companion upload.
 *
 * @param response the response from the companion upload
 */
export function buildOnCompleteEvent(response: any): UppyComponentEvent {
  let error = null;
  let success = null;
  let status: UppyComponentEventStatus = UppyComponentEventStatus.SUCCESS;

  if (response.failed.length > 1 && response.successful.length === 0) {
    error = response.failed;
    status = UppyComponentEventStatus.FAILURE;
  } else if (response.failed.length > 0 && response.successful.length > 0) {
    error = response.failed;
    status = UppyComponentEventStatus.PARTIAL;
  } else if (response.failed.length === 0 && response.successful.length > 0) {
    success = response.successful;
  }

  return {
    type: UppyComponentEventType.ON_COMPLETE,
    status,
    error,
    success,
  };
}

/**
 * Builds a UppyComponentEvent for the onError event from a response from companion upload.
 *
 * @param file that was trying to be uploaded.
 * @param error the response from the companion upload
 */
export function buildOnErrorEvent(file: any, error?: any): UppyComponentEvent {
  return {
    type: UppyComponentEventType.ON_FAILURE,
    status: UppyComponentEventStatus.FAILURE,
    file,
    error,
  };
}

/**
 * Builds a UppyComponentEvent for the onSuccess event from a response from companion upload.
 *
 * @param status the UppyComponentEventStatus based on the call to our server.
 * @param response the response from the companion upload
 * @param uploadState upload state of asset.
 * @param contentBlockData content block data.
 */
export function buildOnSuccessEvent(status: UppyComponentEventStatus, response: any, uploadState?: any, contentBlockData?: any): UppyComponentEvent {
  const event: UppyComponentEvent = {
    type: UppyComponentEventType.ON_SUCCESS,
    status,
    uploadState,
    contentBlockData,
  };
  if (status !== UppyComponentEventStatus.FAILURE && status !== UppyComponentEventStatus.PROCESSING_FAILURE) {
    event.response = response;
    if (event.uploadState === 'imageCarouselSlide') {
      const handleCarouselSlideEvent = new CustomEvent('imageCarouselAddSlide', {
        detail: {
          data: event,
        },
      });
      window.dispatchEvent(handleCarouselSlideEvent);
    }
  } else {
    event.error = response;
  }

  return event;
}
